/**
 * ECharts Pie/Donut implementation. Config-driven, with hover scale to match
 * the renderActiveShape behavior in the other libs.
 */

import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { PieChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = PieChartProps & CommonChartProps;

export function EchartsPie({ type, data, minLabelPercent = 0.08 }: Props): JSX.Element {
  const cfg = chartTheme('echarts');
  const palette = cfg.theme.categoricalPalette;
  const isDonut = type === 'donut';

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        backgroundColor: 'transparent',
        borderWidth: 0,
        padding: 0,
        extraCssText: 'box-shadow: none;',
        formatter: (params: { dataIndex: number; data: { value: number; name: string; itemStyle: { color: string } }; percent: number }) => {
          return renderToStaticMarkup(
            <DrillAwareTooltip
              rows={[
                {
                  color: params.data.itemStyle.color,
                  label: params.data.name,
                  value: params.data.value,
                },
              ]}
            />
          );
        },
      },
      series: [
        {
          type: 'pie',
          radius: isDonut ? ['55%', '88%'] : ['0%', '88%'],
          // ECharts handles label visibility threshold via labelLine.show conditionally;
          // we approximate via a minPercent guard in label formatter.
          label: {
            show: true,
            formatter: (params: { percent: number }) =>
              params.percent >= minLabelPercent * 100 ? `${params.percent.toFixed(0)}%` : '',
            color: '#ffffff',
            fontSize: 11,
            fontWeight: 600,
            position: 'inside',
          },
          labelLine: { show: false },
          emphasis: {
            scale: true,
            scaleSize: 6,
          },
          data: data.map((d, i) => ({
            value: d.value,
            name: d.label,
            itemStyle: { color: (d.color as string) ?? palette[i % palette.length] },
          })),
        },
      ],
    }),
    [data, isDonut, minLabelPercent, palette]
  );

  return <ReactECharts option={option} style={{ width: '100%', height: '100%' }} notMerge lazyUpdate />;
}
